<template>
    <v-alert v-if="hint && !dismissed" class="sc-chat-hint mx-auto" max-width="720" dense text border="left" color="primary" icon="mdi-google" dismissible @input="dismiss">
        <div class="sc-chat-hint-body">
            <div class="sc-chat-hint-text">
                <div class="font-weight-bold">{{ title }}</div>
                <div>{{ text }}</div>
                <div v-if="errorMessage" class="error--text mt-1">{{ errorMessage }}</div>
            </div>
            <v-btn v-if="hint === 'no_properties'" small rounded depressed color="primary" @click="openSettings">Choose properties</v-btn>
            <v-btn v-else small rounded depressed color="primary" :loading="connecting" @click="connect">
                <v-icon small class="mr-1">mdi-google</v-icon>
                {{ hint === "reauth_required" ? "Reconnect Google" : "Connect with Google" }}
            </v-btn>
        </div>
    </v-alert>
</template>

<script>
    // Hinweis auf der Startseite des AI Chats: Search Console ist noch nicht verbunden (oder abgelaufen, oder ohne freigegebene
    // Property). Nur für Admins der Firma - alle anderen sehen nichts, denn nur Admins können etwas daran ändern. Gezeigt wird er
    // nur über die searchinsights-API (VUE_APP_GSC_VIA_API) und nur, wenn der Chat auf diesem Server die Search-Console-Tools hat.
    // Der Google-Login verlässt die Seite und kommt auf Einstellungen > Integrations zurück (fester Rücksprung der Monitoring-API).
    import { describeSearchConsoleError, searchConsoleChatHint, searchConsoleErrorCode, searchConsoleViaApi, statusFromGscApi } from "../utils/searchConsole"

    const DISMISS_KEY = "quaro_gsc_chat_hint_dismissed"
    // Status 60 s merken: die Chat-Seite fragt sonst bei jedem neuen Chat und jedem Zurückkehren erneut (Rate-Limit pro IP)
    const STATUS_TTL_MS = 60000
    let statusCache = { company: null, at: 0, status: null }

    export default {
        name: "SearchConsoleChatHint",
        data() {
            return {
                status: null,
                connecting: false,
                errorMessage: "",
                dismissed: this.readDismissed()
            }
        },
        computed: {
            hint() {
                return searchConsoleViaApi() ? searchConsoleChatHint(this.status) : null
            },
            title() {
                if (this.hint === "reauth_required") return "Your Google Search Console connection has expired"
                if (this.hint === "no_properties") return "No Search Console property is enabled yet"
                return "Google Search Console is not connected yet"
            },
            text() {
                if (this.hint === "reauth_required") return "Reconnect Google so Quaro AI can answer questions about your clicks, impressions and rankings again."
                if (this.hint === "no_properties") return "Google is connected, but Quaro AI may only read the properties you enable. Choose which ones in the integration settings."
                return "Connect it once for your whole company and Quaro AI can answer questions about your own clicks, impressions, queries, pages and index status."
            }
        },
        methods: {
            readDismissed() {
                try {
                    return sessionStorage.getItem(DISMISS_KEY) === "1"
                } catch (error) {
                    return false
                }
            },
            dismiss() {
                this.dismissed = true
                try {
                    sessionStorage.setItem(DISMISS_KEY, "1")
                } catch (error) {
                    // ohne sessionStorage bleibt das Ausblenden nur für diese Seitenansicht
                }
            },
            gscPost(path) {
                return this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/gsc" + path, { user_id: localStorage.id, company_id: localStorage.company_id, token: localStorage.token })
            },
            // Ein Fehler beim Status (Session abgelaufen, API nicht erreichbar) zeigt keinen Hinweis - der Chat selbst ist davon nicht betroffen.
            async refresh() {
                if (!searchConsoleViaApi() || !localStorage.token) return
                const company = localStorage.company_id
                if (statusCache.company === company && Date.now() - statusCache.at < STATUS_TTL_MS) {
                    this.status = statusCache.status
                    return
                }
                statusCache = { company, at: Date.now(), status: null }
                try {
                    this.status = statusFromGscApi((await this.gscPost("/status")).data)
                } catch (error) {
                    this.status = null
                }
                if (statusCache.company === company) statusCache.status = this.status
            },
            async connect() {
                this.connecting = true
                this.errorMessage = ""
                try {
                    const response = await this.gscPost("/connect")
                    const target = response.data && response.data.start_url
                    if (target) {
                        window.location.assign(target)
                        return
                    }
                    this.errorMessage = describeSearchConsoleError(searchConsoleErrorCode(response))
                } catch (error) {
                    this.errorMessage = describeSearchConsoleError(searchConsoleErrorCode(error))
                }
                this.connecting = false
            },
            openSettings() {
                this.$router.push({ path: "/settings", query: { tab: "integrations" } }).catch(() => {})
            }
        },
        mounted() {
            this.refresh()
        }
    }
</script>

<style scoped>
    .sc-chat-hint {
        font-size: 0.875rem;
        text-align: left;
    }
    .sc-chat-hint-body {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
    }
    .sc-chat-hint-text {
        flex: 1 1 320px;
    }
</style>
